import {Anchor} from '@translated/babele-react'
import clsx from 'clsx'
import {Trans, useTranslation} from 'react-i18next'
import {Link, MetaFunction, ActionFunctionArgs} from 'react-router'
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListItem,
  Paragraph
} from '~/ui/termsAndPoliciesTypography'
import {enrichedMeta} from '~/utils/meta.server'

export const handle = {
  i18n: 'terms-and-conditions'
}

export const meta: MetaFunction<typeof loader> = ({data}) => {
  if (!data?.meta) return []

  return data.meta
}

export const loader = async ({request}: ActionFunctionArgs) => {
  return {
    meta: await enrichedMeta({
      with: ['og'],
      request,
      namespace: 'terms-and-conditions'
    })
  }
}

export {ErrorBoundary} from '~/features/RootErrorBoundary'

export default function TermsAndConditions() {
  const {t} = useTranslation('terms-and-conditions')

  return (
    <main className="flex grow justify-center gap-2 lg:mb-2 lg:px-2" data-ignore-max-height="true">
      <div
        className={clsx(
          'text-neutral-1000',
          'lg:rounded-xl',
          'flex w-full flex-col gap-10 rounded-t-xl bg-neutral-25 px-8 py-[6rem] transition-colors'
        )}
      >
        <div className="flex flex-col items-center gap-8">
          <h1 className="text-center text-display-lg font-bold">{t('title')}</h1>
          <div className="flex w-full max-w-[45rem] flex-col justify-start">
            <Heading1>{t('section_1_title')}</Heading1>
            <Heading2>{t('section_1_1_title')}</Heading2>
            <Paragraph>{t('section_1_1_content.0')}</Paragraph>
            <Paragraph>
              <Trans
                t={t}
                i18nKey={'section_1_1_content.1'}
                components={{
                  1: <Anchor href="https://translated.com" />,
                  2: <Anchor href="mailto:info@translated.com" />
                }}
              />
            </Paragraph>
            <Paragraph>{t('section_1_1_content.2')}</Paragraph>
            <Heading2>{t('section_1_2_title')}</Heading2>
            <Paragraph>{t('section_1_2_content')}</Paragraph>
            <Heading2>{t('section_1_3_title')}</Heading2>
            <Paragraph>{t('section_1_3_content')}</Paragraph>
            <Heading2>{t('section_1_4_title')}</Heading2>
            <Paragraph>{t('section_1_4_content')}</Paragraph>
            <Heading2>{t('section_1_5_title')}</Heading2>
            <Paragraph>{t('section_1_5_content')}</Paragraph>
            <Heading2>{t('section_1_6_title')}</Heading2>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="section_1_6_content"
                components={{1: <Anchor href="/privacy-policy" />}}
              />
            </Paragraph>
            <Heading2 id="p1.7">{t('section_1_7_title')}</Heading2>
            <Paragraph>{t('section_1_7_content')}</Paragraph>
            {/* SECTION 2 */}
            <Heading1>{t('section_2_title')}</Heading1>
            <Heading2>{t('section_2_1_title')}</Heading2>
            <Paragraph>{t('section_2_1_content')}</Paragraph>
            <Heading2>{t('section_2_2_title')}</Heading2>
            <Paragraph>{t('section_2_2_content')}</Paragraph>
            <Heading2>{t('section_2_3_title')}</Heading2>
            <Paragraph>{t('section_2_3_content')}</Paragraph>
            <Heading3>{t('section_2_3_1_title')}</Heading3>
            <Paragraph>{t('section_2_3_1_content')}</Paragraph>
            <Heading3>{t('section_2_3_2_title')}</Heading3>
            <Paragraph>{t('section_2_3_2_content')}</Paragraph>
            <Heading3>{t('section_2_3_3_title')}</Heading3>
            <Paragraph>{t('section_2_3_3_content')}</Paragraph>
            <Heading2>{t('section_2_4_title')}</Heading2>
            <Paragraph>{t('section_2_4_content.0')}</Paragraph>
            <Paragraph>{t('section_2_4_content.1')}</Paragraph>

            <Heading2>{t('section_2_5_title')}</Heading2>
            <Paragraph>{t('section_2_5_content.0')}</Paragraph>
            <Paragraph>{t('section_2_5_content.1')}</Paragraph>
            <List>
              <ListItem>{t('section_2_5_list.item_1')}</ListItem>
              <ListItem>{t('section_2_5_list.item_2')}</ListItem>
              <ListItem>{t('section_2_5_list.item_3')}</ListItem>
            </List>

            <Paragraph>{t('section_2_5_content.2')}</Paragraph>

            <List>
              <ListItem>{t('section_2_5_list_2.item_1')}</ListItem>
              <ListItem>{t('section_2_5_list_2.item_2')}</ListItem>
              <ListItem>{t('section_2_5_list_2.item_3')}</ListItem>
            </List>

            <Paragraph>{t('section_2_5_content.3')}</Paragraph>

            {/* SECTION 3 */}
            <Heading1 id="p3">{t('section_3_title')}</Heading1>
            <Paragraph>{t('section_3_content.0')}</Paragraph>
            <Paragraph>{t('section_3_content.1')}</Paragraph>
            <Heading2>{t('section_3_1_title')}</Heading2>
            <Paragraph>{t('section_3_1_content')}</Paragraph>
            <List>
              <ListItem>{t('section_3_1_list.item_1')}</ListItem>
              <ListItem>{t('section_3_1_list.item_2')}</ListItem>
            </List>

            <Heading2>{t('section_3_2_title')}</Heading2>
            <Paragraph>{t('section_3_2_content')}</Paragraph>
            <List>
              <ListItem>{t('section_3_2_list.item_1')}</ListItem>
              <ListItem>{t('section_3_2_list.item_2')}</ListItem>
              <ListItem>{t('section_3_2_list.item_3')}</ListItem>
            </List>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="section_3_2_additional_info"
                components={{1: <Anchor render={<Link to="#p6.3" />} />}}
              />
            </Paragraph>

            {/* SECTION 4 */}
            <Heading1>{t('section_4_title')}</Heading1>
            <Heading2>{t('section_4_1_title')}</Heading2>
            <Paragraph>{t('section_4_1_content')}</Paragraph>

            <Heading2>{t('section_4_2_title')}</Heading2>
            <Paragraph>{t('section_4_2_content')}</Paragraph>
            <List>
              <ListItem>{t('section_4_2_list.item_1')}</ListItem>
              <ListItem>{t('section_4_2_list.item_2')}</ListItem>
              <ListItem>{t('section_4_2_list.item_3')}</ListItem>
            </List>
            <Paragraph>{t('section_4_2_additional_info')}</Paragraph>

            <Heading2>{t('section_4_3_title')}</Heading2>
            <Paragraph>{t('section_4_3_content')}</Paragraph>
            <List>
              <ListItem>{t('section_4_3_list.item_1')}</ListItem>
              <ListItem>{t('section_4_3_list.item_2')}</ListItem>
              <ListItem>{t('section_4_3_list.item_3')}</ListItem>
              <ListItem>{t('section_4_3_list.item_4')}</ListItem>
            </List>
            <Paragraph>{t('section_4_3_additional_info')}</Paragraph>

            <Heading2>{t('section_4_4_title')}</Heading2>
            <Paragraph>{t('section_4_4_content')}</Paragraph>
            <List>
              <ListItem>{t('section_4_4_list.item_1')}</ListItem>
              <ListItem>{t('section_4_4_list.item_2')}</ListItem>
              <ListItem>{t('section_4_4_list.item_3')}</ListItem>
            </List>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="section_4_4_additional_info"
                components={{1: <Anchor render={<Link to="#p8.1" />} />}}
              />
            </Paragraph>

            <Heading2>{t('section_4_5_title')}</Heading2>
            <Paragraph>{t('section_4_5_content')}</Paragraph>

            <Heading2>{t('section_4_6_title')}</Heading2>
            <Paragraph>{t('section_4_6_content')}</Paragraph>

            <Heading2>{t('section_4_7_title')}</Heading2>
            <Paragraph>{t('section_4_7_content')}</Paragraph>

            {/* SECTION 5 */}
            <Heading1>{t('section_5_title')}</Heading1>
            <Heading2>{t('section_5_1_title')}</Heading2>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="section_5_1_content"
                components={{1: <Anchor render={<Link to="#p1.7" />} />}}
              />
            </Paragraph>

            <Heading2>{t('section_5_2_title')}</Heading2>
            <Paragraph>{t('section_5_2_content')}</Paragraph>

            <Heading2>{t('section_5_3_title')}</Heading2>
            <Paragraph>{t('section_5_3_content')}</Paragraph>

            <Heading2>{t('section_5_4_title')}</Heading2>
            <Paragraph>{t('section_5_4_content')}</Paragraph>

            <Heading2>{t('section_5_5_title')}</Heading2>
            <Paragraph>{t('section_5_5_content')}</Paragraph>

            <Heading2>{t('section_5_6_title')}</Heading2>
            <Paragraph>{t('section_5_6_content')}</Paragraph>

            {/* SECTION 6 */}
            <Heading1>{t('section_6_title')}</Heading1>
            <Heading2>{t('section_6_1_title')}</Heading2>
            <Paragraph>{t('section_6_1_content')}</Paragraph>

            <Heading2>{t('section_6_2_title')}</Heading2>
            <Paragraph>{t('section_6_2_content')}</Paragraph>

            <Heading2 id="p6.3">{t('section_6_3_title')}</Heading2>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="section_6_3_content.0"
                components={{1: <Anchor render={<Link to="#p3" />} />}}
              />
            </Paragraph>
            <Paragraph>{t('section_6_3_content.1')}</Paragraph>

            <Heading2>{t('section_6_4_title')}</Heading2>
            <Paragraph>{t('section_6_4_content')}</Paragraph>

            {/* SECTION 7 */}
            <Heading1>{t('section_7_title')}</Heading1>
            <Paragraph>{t('section_7_content')}</Paragraph>

            {/* SECTION 8 */}
            <Heading1>{t('section_8_title')}</Heading1>
            <Paragraph>{t('section_8_content')}</Paragraph>
            <List>
              <ListItem>{t('section_8_list.item_1')}</ListItem>
              <ListItem>{t('section_8_list.item_2')}</ListItem>
              <ListItem>{t('section_8_list.item_3')}</ListItem>
              <ListItem>{t('section_8_list.item_4')}</ListItem>
              <ListItem>{t('section_8_list.item_5')}</ListItem>
              <ListItem>{t('section_8_list.item_6')}</ListItem>
              <ListItem>{t('section_8_list.item_7')}</ListItem>
            </List>
            <Paragraph>{t('section_8_additional_info')}</Paragraph>
            <Heading2 id="p8.1">{t('section_8_1_title')}</Heading2>
            <Paragraph>{t('section_8_1_content.0')}</Paragraph>
            <Paragraph>{t('section_8_1_content.1')}</Paragraph>
            <Paragraph>{t('section_8_1_content.2')}</Paragraph>

            {/* SECTION 9 */}
            <Heading1>{t('section_9_title')}</Heading1>
            <Paragraph>{t('section_9_content.0')}</Paragraph>
            <Paragraph>{t('section_9_content.1')}</Paragraph>

            {/* Footer */}
            <br />
            <br />
            <Paragraph>
              <strong>{t('version_info_title')}</strong>
              <br />
              <i>
                {t('version_number')}
                <br />
                <br />
                {t('version_last_update')}
              </i>
            </Paragraph>
          </div>
        </div>
      </div>
    </main>
  )
}
